import cx from 'classnames'
import { formatDistanceToNow, parseISO } from 'date-fns'
import { friendlyConstant, issue } from './RecoveryCertificate'
import { RecoveryCertificateLink } from './RecoveryCertificateLink'
import { RecoveryCertificateQuery_recoveryCertificates } from './__generated__/RecoveryCertificateQuery'

type recoveryCertificates = RecoveryCertificateQuery_recoveryCertificates & {
  handled?: boolean
}

export const RecoveryCertificateLine = ({
  recoveryCertificate,
  onReviewDocumentation,
}: {
  recoveryCertificate: recoveryCertificates
  onReviewDocumentation: () => void
}) => {
  const { status, claimedAt, claimedBy, createdAt, paymentStatus } = recoveryCertificate

  const queuedTime = createdAt

  return (
    <>
      <tr>
        <td>
          <p>
            <RecoveryCertificateLink recoveryCertificate={recoveryCertificate} />
          </p>
          <hr style={{ margin: '0.5rem' }} />
          {recoveryCertificate?.handled ? (
            <p className="is-small is-success">Already Handled</p>
          ) : (
            <p>
              <strong>Waiting: </strong>
              {queuedTime && formatDistanceToNow(parseISO(queuedTime))}
            </p>
          )}
        </td>
        <td className={highlightIssue(status, paymentStatus, claimedBy)}>
          <p>{issue(status, paymentStatus)}</p>
          <hr style={{ margin: '0.5rem' }} />
          {claimedBy && (
            <p>
              <strong>Claimed by: </strong>
              {claimedBy}
              {', '}
              {formatDistanceToNow(parseISO(claimedAt), {
                addSuffix: true,
              })}
            </p>
          )}
        </td>
        <td>{friendlyConstant(status)}</td>
        <td
          className={
            paymentStatus === 'PAID'
              ? 'has-background-success-light'
              : paymentStatus === 'REFUNDED'
              ? 'has-background-info-light'
              : 'has-background-danger-light'
          }
        >
          {friendlyConstant(paymentStatus)}
        </td>
        <td>
          {paymentStatus !== 'REFUNDED' &&
            [
              'NEW',
              'REVIEWING_DOCUMENTATION',
              'AWAITING_RESPONSE_FROM_CUSTOMER',
              'DOCUMENTATION_COMPLETE',
              'FINAL_REVIEW',
            ].includes(status) && (
              <button
                className={cx('button is-small is-info is-fullwidth', {
                  'is-outlined': claimedAt,
                })}
                disabled={recoveryCertificate?.handled}
                onClick={onReviewDocumentation}
              >
                {recoveryCertificate?.handled
                  ? 'Already Handled'
                  : paymentStatus === 'PAID'
                  ? 'Verify Documentation'
                  : 'Edit Order'}
              </button>
            )}
        </td>
      </tr>
    </>
  )
}

export const highlightIssue = (
  status: RecoveryCertificateQuery_recoveryCertificates['status'],
  paymentStatus: RecoveryCertificateQuery_recoveryCertificates['paymentStatus'],
  claimedBy: RecoveryCertificateQuery_recoveryCertificates['claimedBy']
) => {
  if (claimedBy) {
    return 'has-background-warning-light'
  }

  switch (status) {
    case 'AWAITING_RESPONSE_FROM_CUSTOMER':
      return 'has-background-info-light'
  }

  switch (paymentStatus) {
    case 'UNPAID':
    case 'CANCELLED_DURING_CHECKOUT':
    case 'PAYMENT_FAILED':
      return 'has-background-danger-light'
    case 'REFUNDED':
      return 'has-background-grey-lighter'
  }

  return 'has-background-success-light'
}

export default RecoveryCertificateLine
